import {
  Badge,
  Box,
  Card,
  Center,
  Container,
  Group,
  Loader,
  Paper,
  RingProgress,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { IconActivity, IconCheck, IconServer, IconWorld, IconX } from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { env } from "~/env";


export const Route = createFileRoute("/")({
  component: HomeComponent,
});

function HomeComponent() {
  return (
    <Box
      style={{
        minHeight: "100vh",
        backgroundColor: "var(--mantine-color-dark-8)",
        backgroundImage: `
          radial-gradient(at 0% 0%, hsla(210, 40%, 10%, 1) 0, transparent 50%),
          radial-gradient(at 50% 0%, hsla(220, 40%, 15%, 1) 0, transparent 50%),
          radial-gradient(at 100% 0%, hsla(230, 40%, 10%, 1) 0, transparent 50%)
        `,
        position: "relative",
        overflow: "hidden",
        color: "var(--mantine-color-text)",
      }}>
      {/* Ambient background animations - adjusted for light theme */}
      <Box
        style={{
          position: "absolute",
          top: "10%",
          left: "5%",
          width: "400px",
          height: "400px",
          background: "radial-gradient(circle, rgba(99, 102, 241, 0.08) 0%, transparent 70%)",
          filter: "blur(60px)",
          animation: "float 12s ease-in-out infinite",
        }}
      />
      <Box
        style={{
          position: "absolute",
          bottom: "10%",
          right: "5%",
          width: "500px",
          height: "500px",
          background: "radial-gradient(circle, rgba(139, 92, 246, 0.08) 0%, transparent 70%)",
          filter: "blur(60px)",
          animation: "float 16s ease-in-out infinite reverse",
        }}
      />

      <Container size="sm" h="100vh" style={{ position: "relative", zIndex: 10 }}>
        <Center h="100%">
          <Stack w="100%" align="center" gap="xl">
            <Card
              shadow="xl"
              radius="lg"
              p={0}
              w="100%"
              maw={500}
              style={{
                backgroundColor: "rgba(25, 25, 25, 0.8)",
                backdropFilter: "blur(20px)",
                border: "1px solid var(--mantine-color-dark-4)",
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
                overflow: "hidden",
              }}>
              {/* Card Header */}
              <Box
                h={120}
                style={{
                  background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    backgroundImage: "radial-gradient(rgba(255,255,255,0.2) 1px, transparent 1px)",
                    backgroundSize: "20px 20px",
                    opacity: 0.2,
                  }}
                />
                <Stack gap={4} align="center" style={{ zIndex: 1, color: "white" }}>
                  <Group gap={8}>
                    <IconServer size={28} stroke={2} />
                    <Title order={3} fw={800} lts={1} c="white">
                      SYSTEM STATUS
                    </Title>
                  </Group>
                  <Text size="xs" opacity={0.9} tt="uppercase" fw={600} lts={2} c="white">
                    Perforia CRM Monitor
                  </Text>
                </Stack>
              </Box>

              {/* Card Body */}
              <Box p="xl">
                <ApiStatus />
              </Box>
            </Card>

            <Text size="xs" c="dimmed" style={{ opacity: 0.7 }}>
              &copy; {new Date().getFullYear()} Perforia CRM. All systems operational.
            </Text>
          </Stack>
        </Center>
      </Container>

      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translateY(0) scale(1); }
            50% { transform: translateY(-20px) scale(1.05); }
          }
        `}
      </style>
    </Box>
  );
}

function ApiStatus() {
  const [responseTime, setResponseTime] = useState<number>();
  const [status, setStatus] = useState<"loading" | "connected" | "error">("loading");

  useEffect(() => {
    async function ping() {
      try {
        const start = performance.now();
        const res = await fetch(`${env.VITE_SERVER_URL}/ping`, { cache: "no-store" });
        const end = performance.now();
        setResponseTime(Math.round(end - start));
        setStatus(res.ok ? "connected" : "error");
      } catch {
        setStatus("error");
        setResponseTime(undefined);
      }
    }

    ping();
    const id = setInterval(ping, 5_000);
    return () => clearInterval(id);
  }, []);

  const isConnected = status === "connected";
  const isError = status === "error";

  let statusColor = "gray";
  let statusText = "Checking...";
  let statusIcon = <Loader size={22} color="gray" />;

  if (isConnected) {
    statusColor = "teal";
    statusText = "Operational";
    statusIcon = <IconCheck size={28} style={{ color: "var(--mantine-color-teal-6)" }} stroke={3} />;
  } else if (isError) {
    statusColor = "red";
    statusText = "Disconnected";
    statusIcon = <IconX size={28} style={{ color: "var(--mantine-color-red-6)" }} stroke={3} />;
  }

  return (
    <Stack gap="xl">
      <Group justify="space-between" wrap="nowrap" align="center">
        <Group gap="md">
          <RingProgress
            size={64}
            thickness={6}
            roundCaps
            sections={[{ value: 100, color: statusColor }]}
            label={<Center>{statusIcon}</Center>}
          />
          <Stack gap={2}>
            <Text size="lg" fw={700} c="text" lh={1.2}>
              {statusText}
            </Text>
            <Text size="xs" c="dimmed">
              {isConnected ? "Backend API is reachable" : isError ? "Connection failed" : "Pinging server..."}
            </Text>
          </Stack>
        </Group>

        {isConnected && (
          <Badge variant="light" color="teal" size="lg" radius="sm">
            LIVE
          </Badge>
        )}
      </Group>

      {/* Metrics Grid */}
      <Stack gap="sm">
        <Paper
          p="md"
          radius="md"
          style={{
            backgroundColor: "var(--mantine-color-dark-6)",
            border: "1px solid var(--mantine-color-dark-4)",
          }}>
          <Stack gap="xs">
            <Group justify="space-between">
              <Group gap={8} align="center">
                <ThemeIcon size="sm" variant="transparent" c="dimmed">
                  <IconWorld size={16} />
                </ThemeIcon>
                <Text size="sm" c="dimmed">
                  Endpoint
                </Text>
              </Group>
              <Text size="sm" c="text" style={{ fontFamily: "monospace" }}>
                {env.VITE_SERVER_URL.replace(/^https?:\/\//, "")}
              </Text>
            </Group>

            <Group justify="space-between">
              <Group gap={8} align="center">
                <ThemeIcon size="sm" variant="transparent" c="dimmed">
                  <IconActivity size={16} />
                </ThemeIcon>
                <Text size="sm" c="dimmed">
                  Response Time
                </Text>
              </Group>
              <Text size="sm" c={isConnected ? "teal.7" : "dimmed"} fw={600}>
                {responseTime ? `${responseTime}ms` : "-"}
              </Text>
            </Group>
          </Stack>
        </Paper>
      </Stack>
    </Stack>
  );
}
